import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";

import { formatMoney } from "../../formatters";
import { arActionLabel } from "../InsuranceArDashboard/arActions";

interface BalanceAdjustmentFollowUpDialogProps {
  entry: InsuranceArEntryMessage | null;
  confirmedAction: string;
  patientName: string;
  balanceAdjustmentActions: string[];
  onClose: () => void;
  onConfirm: (action: string) => Promise<void>;
}

export default function BalanceAdjustmentFollowUpDialog({
  entry,
  confirmedAction,
  patientName,
  balanceAdjustmentActions,
  onClose,
  onConfirm,
}: BalanceAdjustmentFollowUpDialogProps) {
  const [action, setAction] = React.useState("");
  const [saving, setSaving] = React.useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping entry would change when this runs
  React.useEffect(() => {
    setAction(
      balanceAdjustmentActions.includes(confirmedAction) ? confirmedAction : "",
    );
    setSaving(false);
  }, [entry, confirmedAction, balanceAdjustmentActions]);

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleConfirm = async () => {
    if (!action) return;
    setSaving(true);
    try {
      await onConfirm(action);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={entry !== null} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Set balance adjustment action?</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ marginTop: "8px" }}>
          <Typography sx={{ fontSize: "14px", color: "#027A48" }}>
            ✓ {arActionLabel(confirmedAction)} confirmed
          </Typography>
          {entry ? (
            <Typography sx={{ fontSize: "14px", color: "#475467" }}>
              {patientName} still shows a {formatMoney(entry.totalAr)} balance
              on the latest A/R report. Set the balance adjustment action now,
              or handle it later in Insurance A/R.
            </Typography>
          ) : null}
          <FormControl fullWidth size="small">
            <InputLabel id="balance-adjustment-follow-up-label">
              Balance adjustment action
            </InputLabel>
            <Select
              labelId="balance-adjustment-follow-up-label"
              label="Balance adjustment action"
              value={action}
              onChange={(e) => setAction(e.target.value)}
            >
              {balanceAdjustmentActions.map((option) => (
                <MenuItem key={option} value={option}>
                  {arActionLabel(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Handle in A/R later
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={saving || !action}
        >
          {saving ? "Saving…" : "Set adjustment action now"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
